import React from 'react';
import { Document, Page, View, Text as PdfText, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSummaryCards from '../components/PdfSummaryCards';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR, fmtDate } from '../pdfUtils';

const s = StyleSheet.create({
  page:    { fontFamily: 'NotoSans', paddingBottom: 60 },
  content: { marginHorizontal: 40, marginTop: 12 },
});

const SUMMARY_COLS = [
  { label: 'Member',        flex: 3 },
  { label: 'Total Offered', flex: 1.2, align: 'right' },
  { label: 'Total Paid',    flex: 1.2, align: 'right' },
];

const DETAIL_COLS = [
  { label: 'Category',     flex: 1.8 },
  { label: 'Offered Date', flex: 1.1 },
  { label: 'Paid Date',    flex: 1.1 },
  { label: 'Offered',      flex: 1,   align: 'right' },
  { label: 'Paid',         flex: 1,   align: 'right' },
  { label: 'Pending',      flex: 1,   align: 'right' },
];

const LABEL_FLEX = DETAIL_COLS[0].flex + DETAIL_COLS[1].flex + DETAIL_COLS[2].flex;

export default function MemberReportDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const isSummary = data.viewMode === 'summary';

  /* -- Summary -- */
  if (isSummary) {
    const rows = Array.isArray(data.summaryRows) ? data.summaryRows : [];
    const totOffered = rows.reduce((s, r) => s + (Number(r.offeredAmount) || 0), 0);
    const totPaid    = rows.reduce((s, r) => s + (Number(r.paidAmount)    || 0), 0);

    return (
      <Document>
        <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
          {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Member Contributions - Summary" theme={t} />}
          <View style={s.content}>
            {sections.summaryCards && (
              <PdfSummaryCards theme={t} items={[
                { label: 'Total Members', value: String(rows.length), color: t.primary },
                { label: 'Total Offered', value: fmtINR(totOffered),  color: t.body    },
                { label: 'Total Paid',    value: fmtINR(totPaid),     color: t.statusGreen },
              ]} />
            )}
            <PdfTableHead cols={SUMMARY_COLS} theme={t} />
            {rows.map((row, i) => (
              <PdfTableRow key={i} cols={SUMMARY_COLS} index={i} theme={t}
                values={[row.memberName || '-', fmtINR(row.offeredAmount), fmtINR(row.paidAmount)]}
              />
            ))}
            <PdfTableFoot cols={SUMMARY_COLS}
              values={[`Total (${rows.length} members)`, fmtINR(totOffered), fmtINR(totPaid)]}
              theme={t}
            />
          </View>
          {sections.footer && <PdfFooter theme={t} />}
        </Page>
      </Document>
    );
  }

  /* -- Detail (grouped by member) -- */
  const allRows = Array.isArray(data.rows) ? data.rows : [];
  const totOffered  = allRows.reduce((s, r) => s + (Number(r.offeredAmount)  || 0), 0);
  const totPaid     = allRows.reduce((s, r) => s + (Number(r.paidAmount)     || 0), 0);
  const totPending  = allRows.reduce((s, r) => s + (Number(r.pendingAmount)  || 0), 0);

  const groupOrder = [];
  const groupMap = {};
  allRows.forEach((row) => {
    const key = row.userId || row.memberName;
    if (!groupMap[key]) { groupMap[key] = { name: row.memberName || '-', entries: [] }; groupOrder.push(key); }
    groupMap[key].entries.push(row);
  });
  const groups = groupOrder.map((key) => groupMap[key]);

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'landscape'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Member Contributions - Detail" theme={t} />}
        <View style={s.content}>
          {sections.summaryCards && (
            <PdfSummaryCards theme={t} items={[
              { label: 'Total Members', value: String(groups.length), color: t.primary },
              { label: 'Total Offered', value: fmtINR(totOffered),    color: t.body    },
              { label: 'Total Paid',    value: fmtINR(totPaid),       color: t.statusGreen },
            ]} />
          )}
          <PdfTableHead cols={DETAIL_COLS} theme={t} />
          {groups.map((group) => {
            const gOffered  = group.entries.reduce((s, r) => s + (Number(r.offeredAmount)  || 0), 0);
            const gPaid     = group.entries.reduce((s, r) => s + (Number(r.paidAmount)     || 0), 0);
            const gPending  = group.entries.reduce((s, r) => s + (Number(r.pendingAmount)  || 0), 0);
            return (
              <React.Fragment key={group.name}>
                <View style={{ flexDirection: 'row', backgroundColor: t.light }} wrap={false}>
                  <PdfText style={{ flex: 1, fontSize: 8.5, fontWeight: 'bold', color: t.body, paddingHorizontal: 4, paddingVertical: 4 }}>
                    {group.name}
                  </PdfText>
                </View>
                {group.entries.map((row, i) => (
                  <PdfTableRow key={i} cols={DETAIL_COLS} index={i} theme={t}
                    values={[
                      row.category    || '-',
                      fmtDate(row.offeringDate),
                      fmtDate(row.paidDate) || '-',
                      fmtINR(row.offeredAmount),
                      fmtINR(row.paidAmount),
                      fmtINR(row.pendingAmount),
                    ]}
                  />
                ))}
                <View style={{ flexDirection: 'row', backgroundColor: t.mid }} wrap={false}>
                  <PdfText style={{ flex: LABEL_FLEX, fontSize: 8, fontWeight: 'bold', color: t.body, paddingHorizontal: 4, paddingVertical: 3, textAlign: 'right' }}>
                    Subtotal - {group.entries.length} entr{group.entries.length !== 1 ? 'ies' : 'y'}
                  </PdfText>
                  <PdfText style={{ flex: DETAIL_COLS[3].flex, fontSize: 8, fontWeight: 'bold', color: t.body, paddingHorizontal: 4, paddingVertical: 3, textAlign: 'right' }}>
                    {fmtINR(gOffered)}
                  </PdfText>
                  <PdfText style={{ flex: DETAIL_COLS[4].flex, fontSize: 8, fontWeight: 'bold', color: t.body, paddingHorizontal: 4, paddingVertical: 3, textAlign: 'right' }}>
                    {fmtINR(gPaid)}
                  </PdfText>
                  <PdfText style={{ flex: DETAIL_COLS[5].flex, fontSize: 8, fontWeight: 'bold', color: t.body, paddingHorizontal: 4, paddingVertical: 3, textAlign: 'right' }}>
                    {fmtINR(gPending)}
                  </PdfText>
                </View>
              </React.Fragment>
            );
          })}
          <PdfTableFoot cols={DETAIL_COLS}
            values={[`Grand Total (${allRows.length} entries)`, '', '', fmtINR(totOffered), fmtINR(totPaid), fmtINR(totPending)]}
            theme={t}
          />
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}
